/**
 * PendingInvitesTable - Convites pendentes com copiar/reenviar
 */
import React, { useMemo } from 'react';
import { ClockIcon } from '../../../components/icons';
import { formatDateBR } from '../../../services/utils/serviceUtils';
import DataTable, { Column } from '../../../components/shared/DataTable';

export interface InviteData {
    id: string;
    email: string;
    role: string;
    created_at: string;
    expires_at: string;
    token?: string;
}

interface PendingInvitesTableProps {
    invites: InviteData[];
    isLoading?: boolean;
    onRevoke: (id: string) => void;
    onCopyLink: (token: string) => void;
    onReshare: (id: string) => void;
    onRegenerate: (id: string) => void;
}

const btnClass = 'text-xs font-medium px-2 py-1 rounded border transition-colors disabled:opacity-50';

const PendingInvitesTable: React.FC<PendingInvitesTableProps> = ({
    invites, isLoading, onRevoke, onCopyLink, onReshare, onRegenerate,
}) => {
    const columns: Column<InviteData>[] = useMemo(() => [
        {
            key: 'email', header: 'Email Convidado',
            render: (invite) => <span className="font-medium text-default">{invite.email}</span>,
        },
        {
            key: 'role', header: 'Função',
            render: (invite) => <span className="text-secondary">{invite.role}</span>,
        },
        {
            key: 'created_at', header: 'Enviado em',
            render: (invite) => <span className="text-muted">{formatDateBR(invite.created_at)}</span>,
        },
        {
            key: 'expires_at', header: 'Expira em',
            render: (invite) => (
                <div className="flex items-center text-amber-600">
                    <ClockIcon className="w-4 h-4 mr-1 shrink-0" />
                    {formatDateBR(invite.expires_at)}
                </div>
            ),
        },
        {
            key: 'actions', header: 'Ações', align: 'right',
            render: (invite) => (
                <div className="flex flex-wrap justify-end gap-1">
                    <button
                        type="button"
                        disabled={!invite.token || isLoading}
                        onClick={() => invite.token && onCopyLink(invite.token)}
                        className={`${btnClass} text-blue-600 border-blue-200 hover:bg-accent-primary-subtle`}
                    >
                        Copiar link
                    </button>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => onReshare(invite.id)}
                        className={`${btnClass} text-secondary border-subtle hover:bg-surface-subtle`}
                    >
                        Reenviar
                    </button>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => onRegenerate(invite.id)}
                        className={`${btnClass} text-secondary border-subtle hover:bg-surface-subtle`}
                    >
                        Novo link
                    </button>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => onRevoke(invite.id)}
                        className={`${btnClass} text-red-500 border-red-200 hover:bg-red-50`}
                    >
                        Cancelar
                    </button>
                </div>
            ),
        },
    ], [onRevoke, onCopyLink, onReshare, onRegenerate, isLoading]);

    if (invites.length === 0) return null;

    return (
        <DataTable columns={columns} data={invites} getRowKey={(i) => i.id} emptyMessage="Nenhum convite pendente." />
    );
};

export default PendingInvitesTable;
